import { useState } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import {
  associateSoftwareToken,
  completeNewPassword,
  login,
  submitMfaCode,
  verifySoftwareToken,
  type LoginOutcome,
} from "../auth/cognitoClient";
import { useAuth } from "../auth/AuthContext";

/** TOTP 标准 URI 格式(otpauth://),认证器 App 扫码后自动填入账号名+密钥,
 * 比手动输入密钥更不容易出错(§10 MFA 绑定流程)。
 * issuer 与 verifySoftwareToken() 里的 friendly device name 保持一致。 */
function buildTotpUri(username: string, secretCode: string): string {
  const issuer = "KiroAdminPlatform";
  const label = encodeURIComponent(`${issuer}:${username}`);
  return `otpauth://totp/${label}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;
}

type Step = "CREDENTIALS" | "NEW_PASSWORD" | "MFA_CODE" | "MFA_SETUP";

/**
 * 登录页,对应产品文档 §10(管理平台强制 MFA)。
 *
 * 流程:
 *   1. 用户名+密码 → 根据 Cognito 质询类型分支:
 *      - NEW_PASSWORD_REQUIRED(admin-create-user 创建的账号首次用临时密码登录):
 *        进入 NEW_PASSWORD,要求设置正式密码
 *      - MFA_REQUIRED(已绑定 TOTP):进入 MFA_CODE
 *      - MFA_SETUP_REQUIRED(尚未绑定 TOTP):进入 MFA_SETUP
 *   2. NEW_PASSWORD:提交新密码后,Cognito 会继续返回 MFA_REQUIRED/MFA_SETUP_REQUIRED,
 *      复用 handleChallengeOutcome 统一跳转,不需要单独处理
 *   3. MFA_CODE:提交动态验证码即可登录
 *   4. MFA_SETUP(首次绑定 MFA):展示绑定密钥(需手动输入到 TOTP App,如 Google
 *      Authenticator,未引入二维码生成库以减少依赖面),验证一次动态码完成绑定
 */
export function LoginPage() {
  const [step, setStep] = useState<Step>("CREDENTIALS");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);
  const [secretCode, setSecretCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  /** 统一处理 login()/completeNewPassword() 的质询结果,跳到对应步骤或直接登录成功 */
  const handleChallengeOutcome = async (outcome: LoginOutcome) => {
    if (outcome.status === "SUCCESS") {
      await refresh();
      navigate("/dashboard");
    } else if (outcome.status === "NEW_PASSWORD_REQUIRED") {
      setPendingUser(outcome.cognitoUser);
      setStep("NEW_PASSWORD");
    } else if (outcome.status === "MFA_REQUIRED") {
      setPendingUser(outcome.cognitoUser);
      setStep("MFA_CODE");
    } else {
      const code = await associateSoftwareToken(outcome.cognitoUser);
      setSecretCode(code);
      setPendingUser(outcome.cognitoUser);
      setStep("MFA_SETUP");
      try {
        const dataUrl = await QRCode.toDataURL(buildTotpUri(username, code));
        setQrDataUrl(dataUrl);
      } catch {
        // 二维码生成失败不影响绑定流程,仍可用下方展示的密钥手动输入
        setQrDataUrl(null);
      }
    }
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const outcome = await login(username, password);
      await handleChallengeOutcome(outcome);
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser) return;
    setError(null);
    setLoading(true);
    try {
      const outcome = await completeNewPassword(pendingUser, newPassword);
      await handleChallengeOutcome(outcome);
    } catch (err) {
      setError(err instanceof Error ? err.message : "设置新密码失败");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser) return;
    setError(null);
    setLoading(true);
    try {
      await submitMfaCode(pendingUser, mfaCode);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "验证码校验失败");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser) return;
    setError(null);
    setLoading(true);
    try {
      await verifySoftwareToken(pendingUser, mfaCode);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "MFA 绑定失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <div className="card" style={{ width: 360 }}>
        <h2 style={{ marginTop: 0 }}>Kiro 用户管理平台</h2>
        {error && <div className="error-banner">{error}</div>}

        {step === "CREDENTIALS" && (
          <form onSubmit={handleCredentialsSubmit}>
            <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <label>邮箱/用户名</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)} required />
            </div>
            <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <label>密码</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="primary" type="submit" disabled={loading}>
              {loading ? "登录中..." : "登录"}
            </button>
          </form>
        )}

        {step === "NEW_PASSWORD" && (
          <form onSubmit={handleNewPasswordSubmit}>
            <p style={{ fontSize: 13, color: "#475569" }}>
              首次登录需设置正式密码(不能继续使用邮件里的临时密码),至少 12 位,
              需包含大小写字母、数字和符号。
            </p>
            <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <label>新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <button className="primary" type="submit" disabled={loading}>
              {loading ? "设置中..." : "设置新密码"}
            </button>
          </form>
        )}

        {step === "MFA_CODE" && (
          <form onSubmit={handleMfaSubmit}>
            <p style={{ fontSize: 13, color: "#475569" }}>请输入 TOTP 认证器动态验证码</p>
            <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <label>验证码</label>
              <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
            </div>
            <button className="primary" type="submit" disabled={loading}>
              {loading ? "验证中..." : "确认"}
            </button>
          </form>
        )}

        {step === "MFA_SETUP" && (
          <form onSubmit={handleMfaSetupSubmit}>
            <p style={{ fontSize: 13, color: "#475569" }}>
              首次登录需绑定 TOTP 认证器(如 Google Authenticator/Microsoft Authenticator)。
              推荐用认证器 App 扫描下方二维码(比手动输入密钥更不容易出错):
            </p>
            {qrDataUrl && (
              <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
                <img src={qrDataUrl} alt="TOTP 绑定二维码" width={200} height={200} />
              </div>
            )}
            <p style={{ fontSize: 13, color: "#475569" }}>
              无法扫码时,也可手动将以下密钥添加到认证器 App:
            </p>
            <div className="card" style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
              {secretCode}
            </div>
            <p style={{ fontSize: 12, color: "#94a3b8" }}>
              验证码提示"不正确或已过期"通常是设备系统时间不准(TOTP 依赖 UTC
              时间戳,与部署 region、所在时区无关),请确认手机/电脑已开启"自动
              设置时间"。
            </p>
            <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <label>绑定后输入验证码</label>
              <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required />
            </div>
            <button className="primary" type="submit" disabled={loading}>
              {loading ? "绑定中..." : "完成绑定并登录"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
